import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { RouteEnum } from '../constantes/RouteEnum';
import { UserSearchResponse } from '../_types/user.types';
import { useSchema } from './useSchema';
import { useSchemaLoader } from './useSchemaLoader';
import { useOtpContext } from '../context/OtpContextProvider';

export const useNavigationFlow = () => {
  const navigate = useNavigate();
  const {checkIsOpenFlow} = useSchema();
  const {getAuthenticationType, waitForSchema} = useSchemaLoader();
  const {setCurrentFlow} = useOtpContext();

  const navigateToLogin = useCallback((authenticationType: string) => {
    console.log('navigateToLogin - authenticationType received:', authenticationType);
    
    // Garantir que o authenticationType seja sempre uma string válida
    const normalizedAuthType = authenticationType?.toLowerCase() || 'password';
    
    console.log('navigateToLogin - normalizedAuthType:', normalizedAuthType);
    
    const route = normalizedAuthType === 'otp'
      ? RouteEnum.OTP_REQUEST
      : RouteEnum.LOGIN_PASSWORD;
    
    console.log('navigateToLogin - navigating to route:', route);
    navigate(route);
  }, [navigate]);

  const navigateBasedOnUserData = useCallback(async (userData: UserSearchResponse) => {
    const isOpenFlow = checkIsOpenFlow();
    const isFirstAccess = userData?.firstAccess;
    const isOnboardingFinished = userData?.onboardingFinished;
    const userFound = userData?.userFound;

    console.log('Navigation Flow - userData:', userData);
    console.log('Navigation Flow - isOpenFlow:', isOpenFlow);
    console.log('Navigation Flow - isFirstAccess:', isFirstAccess);
    console.log('Navigation Flow - isOnboardingFinished:', isOnboardingFinished);
    console.log('Navigation Flow - userFound:', userFound);

    // CASO 0: Usuário não encontrado
    if (!userFound) {
      if (isOpenFlow) {
        navigate(RouteEnum.PERSONAL);
      } else {
        console.error('Usuário não encontrado em fluxo fechado');
      }

      return;
    }

    // CASO 1: Fluxo Open - onboarding não finalizado (independente de primeiro acesso)
    if (isOpenFlow && !isOnboardingFinished) {
      navigate(RouteEnum.OTP_REQUEST);

      return;
    }

    // CASO 2: Fluxo Open - onboarding finalizado
    if (isOpenFlow && isOnboardingFinished) {
      console.log('Navegando para login - Fluxo Open, onboarding finalizado');
      try {
        // Aguardar o schema ser carregado antes de obter o authenticationType
        await waitForSchema();
        const authType = getAuthenticationType();
        console.log('Navigation Flow - authType for Open Flow:', authType);
        navigateToLogin(authType);
      } catch (error) {
        console.error('Navigation Flow - Error getting authType for Open Flow:', error);
        // Fallback para password se houver erro
        navigateToLogin('password');
      }

      return;
    }

    // CASO 3: Fluxo Close - primeiro acesso e onboarding não finalizado
    if (!isOpenFlow && isFirstAccess && !isOnboardingFinished) {
      console.log('Navegando para OTP_REQUEST - Fluxo Close, primeiro acesso');
      navigate(RouteEnum.OTP_REQUEST);

      return;
    }

    // CASO 4: Fluxo Close - não é primeiro acesso mas onboarding não finalizado
    if (!isOpenFlow && !isFirstAccess && !isOnboardingFinished) {
      console.log('Navegando para OTP_REQUEST - Fluxo Close, não primeiro acesso');
      navigate(RouteEnum.OTP_REQUEST);

      return;
    }

    // CASO 5: Caso padrão (onboarding finalizado)
    console.log('Navegando para login - Caso padrão');
    try {
      // Aguardar o schema ser carregado antes de obter o authenticationType
      await waitForSchema();
      const authType = getAuthenticationType();
      console.log('Navigation Flow - authType for default case:', authType);
      navigateToLogin(authType);
    } catch (error) {
      console.error('Navigation Flow - Error getting authType for default case:', error);
      // Fallback para password se houver erro
      navigateToLogin('password');
    }
  }, [navigate, checkIsOpenFlow, getAuthenticationType, waitForSchema, navigateToLogin]);

  const navigateToRegister = useCallback(() => {
    navigate(RouteEnum.PERSONAL);
  }, [navigate]);

  const navigateToWelcome = useCallback(() => {
    navigate(RouteEnum.WELCOME);
  }, [navigate]);

  const navigateToUserLogin = useCallback(() => {
    navigate(RouteEnum.LOGIN_USERNAME);
  }, [navigate]);

  const navigateToPasswordReset = useCallback(() => {
    // Configurar o contexto para ResetPassword
    setCurrentFlow('ResetPassword');
    navigate(RouteEnum.FORGOT_PASSWORD_OTP_REQUEST);
  }, [navigate, setCurrentFlow]);

  const navigateToOtpValidation = useCallback(() => {
    navigate(RouteEnum.OTP_VALIDATION);
  }, [navigate]);

  const navigateToFinish = useCallback(() => {
    navigate(RouteEnum.FINISH);
  }, [navigate]);

  return {
    navigateBasedOnUserData,
    navigateToRegister,
    navigateToWelcome,
    navigateToUserLogin,
    navigateToPasswordReset,
    navigateToOtpValidation,
    navigateToFinish,
    navigateToLogin
  };
};
 